import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';

interface DayCell {
  inMonth: boolean;
  day: number | null;
  today: boolean;
}

/**
 * Desktop date-time popup for the product release dates.
 * Calendar, year grid, and hour/minute/AM-PM columns follow the interaction
 * shown in the design reference. Values are committed only when OK is pressed.
 */
@Component({
  selector: 'app-product-datetime-picker',
  templateUrl: './product-datetime-picker.component.html',
  styleUrls: ['./product-datetime-picker.component.scss'],
})
export class ProductDatetimePickerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() value: string | Date | null = null;
  @Input() anchor?: HTMLElement;
  /** Calendar only. Time columns stay hidden and the saved value remains date-only. */
  @Input() dateOnly = false;

  @Output() accepted = new EventEmitter<Date>();
  @Output() dismissed = new EventEmitter<void>();

  view: 'day' | 'year' = 'day';
  ready = false;
  top = 0;
  left = 0;

  year = new Date().getFullYear();
  month = new Date().getMonth();
  day = new Date().getDate();
  hour12 = 12;
  minute = 0;
  meridiem: 'AM' | 'PM' = 'AM';

  minYear = 1900;
  maxYear = 2099;
  years: number[] = [];
  dayCells: DayCell[] = [];
  minutes: number[] = [];

  readonly hours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  readonly weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  readonly monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  @ViewChild('pickerRoot', { static: true }) private rootRef!: ElementRef<HTMLElement>;

  private seconds = 0;
  private milliseconds = 0;
  private timeTouched = false;
  private destroyed = false;
  private revealAttempts = 0;
  private removeListeners: Array<() => void> = [];

  constructor(
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const date = this.parseIncomingValue(this.value);

    this.year = date.getFullYear();
    this.month = date.getMonth();
    this.day = date.getDate();
    this.seconds = date.getSeconds();
    this.milliseconds = date.getMilliseconds();

    const hour24 = date.getHours();
    this.meridiem = hour24 >= 12 ? 'PM' : 'AM';
    this.hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    this.minute = date.getMinutes();

    this.minYear = Math.min(1900, this.year);
    this.maxYear = Math.max(2099, this.year);
    this.years = [];
    for (let year = this.minYear; year <= this.maxYear; year++) {
      this.years.push(year);
    }

    this.minutes = [];
    for (let minute = 0; minute < 60; minute++) {
      this.minutes.push(minute);
    }

    this.rebuildDays();
  }

  ngAfterViewInit(): void {
    const root = this.rootElement;
    if (root && root.parentNode !== document.body) {
      this.renderer.appendChild(document.body, root);
    }
    this.bindReposition();
    this.bindOutsideDismiss();
    this.bindPanelWheel();
    setTimeout(() => this.reveal());
  }

  private reveal(): void {
    if (this.destroyed) {
      return;
    }
    const panel = this.rootElement?.querySelector('.mdt-panel') as HTMLElement | null;
    if ((!panel || !panel.offsetWidth) && this.revealAttempts < 5) {
      this.revealAttempts += 1;
      setTimeout(() => this.reveal());
      return;
    }
    this.position();
    this.scrollTimeIntoView();
    this.watchPageScrollers(this.onPageScroll);
    this.ready = true;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.removeListeners.forEach((remove) => remove());
    this.removeListeners = [];
    const root = this.rootElement;
    const host = this.host.nativeElement;
    if (!root) {
      return;
    }
    if (host.isConnected) {
      host.appendChild(root);
    } else if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  get monthYearLabel(): string {
    return `${this.monthNames[this.month]} ${this.year}`;
  }

  get canGoPrevious(): boolean {
    return this.year > this.minYear || this.month > 0;
  }

  get canGoNext(): boolean {
    return this.year < this.maxYear || this.month < 11;
  }

  pad(value: number): string {
    return value < 10 ? `0${value}` : String(value);
  }

  toggleYearView(): void {
    this.view = this.view === 'year' ? 'day' : 'year';
    if (this.view === 'year') {
      setTimeout(() => this.scrollYearIntoView());
    }
  }

  previousMonth(): void {
    if (!this.canGoPrevious) {
      return;
    }
    if (this.month === 0) {
      this.month = 11;
      this.year -= 1;
    } else {
      this.month -= 1;
    }
    this.clampDay();
    this.rebuildDays();
  }

  nextMonth(): void {
    if (!this.canGoNext) {
      return;
    }
    if (this.month === 11) {
      this.month = 0;
      this.year += 1;
    } else {
      this.month += 1;
    }
    this.clampDay();
    this.rebuildDays();
  }

  selectYear(year: number): void {
    this.year = year;
    this.clampDay();
    this.view = 'day';
    this.rebuildDays();
  }

  selectDay(cell: DayCell): void {
    if (!cell.inMonth || cell.day == null) {
      return;
    }
    this.day = cell.day;
  }

  selectHour(hour: number): void {
    this.hour12 = hour;
    this.timeTouched = true;
  }

  selectMinute(minute: number): void {
    this.minute = minute;
    this.timeTouched = true;
  }

  selectMeridiem(meridiem: 'AM' | 'PM'): void {
    this.meridiem = meridiem;
    this.timeTouched = true;
  }

  accept(): void {
    const hour24 = this.meridiem === 'AM'
      ? (this.hour12 === 12 ? 0 : this.hour12)
      : (this.hour12 === 12 ? 12 : this.hour12 + 12);
    const seconds = this.timeTouched ? 0 : this.seconds;
    const milliseconds = this.timeTouched ? 0 : this.milliseconds;
    this.accepted.emit(new Date(this.year, this.month, this.day, hour24, this.minute, seconds, milliseconds));
  }

  dismiss(): void {
    this.dismissed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.dismiss();
  }

  private get rootElement(): HTMLElement | null {
    return this.rootRef?.nativeElement ?? null;
  }

  /**
   * Date-only strings such as YYYY-MM-DD are read as a local calendar day.
   * Datetime strings keep the existing local Date parsing.
   */
  private parseIncomingValue(value: string | Date | null): Date {
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? new Date() : value;
    }
    if (value == null || String(value).trim() === '') {
      return new Date();
    }
    const trimmed = String(value).trim();
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (dateOnlyMatch) {
      return new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]));
    }
    const parsed = new Date(trimmed);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  private rebuildDays(): void {
    const firstWeekday = new Date(this.year, this.month, 1).getDay();
    const daysInMonth = new Date(this.year, this.month + 1, 0).getDate();
    const today = new Date();
    const cells: DayCell[] = [];

    for (let index = 0; index < firstWeekday; index++) {
      cells.push({ inMonth: false, day: null, today: false });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      cells.push({
        inMonth: true,
        day,
        today: today.getFullYear() === this.year && today.getMonth() === this.month && today.getDate() === day,
      });
    }

    while (cells.length % 7 !== 0) {
      cells.push({ inMonth: false, day: null, today: false });
    }

    this.dayCells = cells;
  }

  private clampDay(): void {
    const lastDay = new Date(this.year, this.month + 1, 0).getDate();
    if (this.day > lastDay) {
      this.day = lastDay;
    }
  }

  private position(): void {
    const panel = this.rootElement?.querySelector('.mdt-panel') as HTMLElement | null;
    const anchor = this.anchor;
    if (!panel || !anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const margin = 8;
    const width = panel.offsetWidth;
    const height = panel.offsetHeight;
    let left = rect.left;
    let top = rect.bottom + 4;

    // Stay on the date box. A right-side field must not slide the popup to the left of the page.
    if (left + width > window.innerWidth - margin) {
      left = rect.right - width;
    }
    if (left < margin) {
      left = margin;
    }
    if (left + width > window.innerWidth - margin) {
      left = window.innerWidth - width - margin;
    }

    const spaceBelow = window.innerHeight - margin - top;
    const spaceAbove = rect.top - margin - 4;
    if (height > spaceBelow && spaceAbove > spaceBelow) {
      top = Math.max(margin, rect.top - height - 4);
    }

    this.top = top;
    this.left = left;
  }

  /**
   * The backdrop does not take clicks, so the page underneath can scroll.
   * A press outside the panel still closes it.
   */
  private bindOutsideDismiss(): void {
    const onPointerDown = (event: Event) => {
      if (this.destroyed) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (this.rootElement?.contains(target) || this.anchor?.contains(target)) {
        return;
      }
      this.dismiss();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    this.removeListeners.push(() => document.removeEventListener('pointerdown', onPointerDown, true));
  }

  /** A wheel over the calendar scrolls the page. Hour, minute, and year lists keep scrolling. */
  private bindPanelWheel(): void {
    const panel = this.rootElement?.querySelector('.mdt-panel');
    if (!panel) {
      return;
    }
    const onWheel = (event: Event) => {
      if (this.destroyed || !(event instanceof WheelEvent) || event.ctrlKey) {
        return;
      }
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('.mdt-time-col, .mdt-years')) {
        return;
      }
      const scroller = this.pageScrollers()[0];
      if (!scroller) {
        return;
      }
      event.preventDefault();
      scroller.scrollTop += event.deltaY;
      this.position();
    };
    panel.addEventListener('wheel', onWheel, { passive: false });
    this.removeListeners.push(() => panel.removeEventListener('wheel', onWheel));
  }

  private watchedScrollers = new Set<EventTarget>();
  private onPageScroll: EventListener = (event?: Event) => {
    if (this.destroyed) {
      return;
    }
    const target = event?.target;
    const root = this.rootElement;
    if (target instanceof Node && root?.contains(target)) {
      return;
    }
    const anchor = this.anchor;
    if (!anchor || !anchor.isConnected) {
      this.dismiss();
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const bounds = anchor.closest('ion-content')?.getBoundingClientRect();
    const visibleTop = bounds?.top ?? 0;
    const visibleBottom = bounds?.bottom ?? window.innerHeight;
    if (rect.bottom < visibleTop + 4 || rect.top > visibleBottom - 4) {
      this.dismiss();
      return;
    }
    this.position();
  };

  private bindReposition(): void {
    const onResize = () => {
      if (!this.destroyed) {
        this.position();
      }
    };

    const content = this.anchor?.closest('ion-content');
    content?.addEventListener('ionScroll', this.onPageScroll);
    this.watchPageScrollers(this.onPageScroll);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', this.onPageScroll, true);

    this.removeListeners.push(() => content?.removeEventListener('ionScroll', this.onPageScroll));
    this.removeListeners.push(() => window.removeEventListener('resize', onResize));
    this.removeListeners.push(() => window.removeEventListener('scroll', this.onPageScroll, true));
  }

  /**
   * Product and track pages emit ionScroll. Partner, legal, marketing, projection,
   * and history pages scroll the ion-content shadow scroller without that event.
   */
  private watchPageScrollers(onScroll: EventListener): void {
    this.pageScrollers().forEach((scroller) => {
      if (this.watchedScrollers.has(scroller)) {
        return;
      }
      this.watchedScrollers.add(scroller);
      scroller.addEventListener('scroll', onScroll);
      this.removeListeners.push(() => scroller.removeEventListener('scroll', onScroll));
    });
  }

  private pageScrollers(): HTMLElement[] {
    const found: HTMLElement[] = [];
    const anchor = this.anchor;
    if (!anchor) {
      return found;
    }

    let content: Element | null = anchor.closest('ion-content');
    while (content) {
      const inner = content.shadowRoot?.querySelector('.inner-scroll');
      if (inner instanceof HTMLElement) {
        found.push(inner);
      }
      content = content.parentElement?.closest('ion-content') ?? null;
    }

    let node = anchor.parentElement;
    while (node && node !== document.body) {
      const overflowY = getComputedStyle(node).overflowY;
      const canScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
      if (canScroll && node.scrollHeight > node.clientHeight + 1) {
        found.push(node);
      }
      node = node.parentElement;
    }

    return found;
  }

  private scrollTimeIntoView(): void {
    const root = this.rootElement;
    if (!root) {
      return;
    }
    root.querySelectorAll('.mdt-time-col').forEach((column) => {
      this.centerSelected(column as HTMLElement);
    });
  }

  private scrollYearIntoView(): void {
    const list = this.rootElement?.querySelector('.mdt-years') as HTMLElement | null;
    if (list) {
      this.centerSelected(list);
    }
  }

  private centerSelected(container: HTMLElement): void {
    const selected = container.querySelector('.selected') as HTMLElement | null;
    if (!selected) {
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const selectedRect = selected.getBoundingClientRect();
    container.scrollTop += selectedRect.top - containerRect.top - container.clientHeight / 2 + selectedRect.height / 2;
  }
}
